import React, { useEffect, useMemo, useRef, useState } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import TablerLayout from "../components/layout/TablerLayout";
import dataService from "../services/dataService";
import { showErrorToast, showSuccessToast } from "../components/toasts/ToastService";

const MODEL_ID_FIELDS = ["szam", "id", "model_id", "modell_szam"];
const MODEL_NAME_FIELDS = ["name", "modell_name", "model_name"];
const MODEL_CODE_FIELDS = ["code", "modell_code", "model_code"];
const AIDFINANCE_COLUMNS = ["C0", "C2", "C3", "C4", "C5"];
const ALLOWED_STATE_META = {
    yes: { label: "Igen", className: "bg-success", symbol: "✓" },
    no: { label: "Nem", className: "bg-danger", symbol: "✕" },
    maybe: { label: "Talán", className: "bg-warning text-dark", symbol: "?" },
};
const CORNER_STATE_META = {
    yes: { label: "Igen", className: "bg-success", symbol: "✓" },
    no: { label: "Nem", className: "bg-danger", symbol: "✕" },
    if: { label: "Kérdőjel", className: "bg-warning text-dark", symbol: "?" },
};
const SPECIAL_NEED_COLUMNS = [
    { key: "is_open_nosed", label: "Nyitott orrú" },
    { key: "spn_nose_cutout", label: "Orrkivágás" },
    { key: "spn_hole_punch", label: "Díszlyukasztás" },
    { key: "spn_deco_stitch", label: "Díszöltés" },
    { key: "spn_tongue", label: "Nyelv" },
    { key: "spn_eyelet", label: "Fűzőszem" },
    { key: "spn_clasp", label: "Cipőszár kapcsozás" },
];

const normalizeApiPayload = (response) => response?.data?.data ?? response?.data ?? null;

const normalizeListPayload = (response) => {
	const payload = normalizeApiPayload(response);
	return Array.isArray(payload) ? payload : [];
};

const normalizeObjectPayload = (response) => {
	const payload = normalizeApiPayload(response);
	return payload && !Array.isArray(payload) ? payload : {};
};

const parseJsonArray = (value) => {
    if (typeof value !== "string" || value.trim() === "") {
        return [];
    }

    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

const normalizeComponentBlockedIds = (value) => {
    if (Array.isArray(value)) {
        return new Set(value.map((item) => String(item).trim()).filter(Boolean));
    }

    if (typeof value === "number") {
        return new Set([String(value)]);
    }

    if (typeof value !== "string" || value.trim() === "") {
        return new Set();
    }

    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
            return new Set(parsed.map((item) => String(item).trim()).filter(Boolean));
        }
    } catch (error) {
        // Fallback to loose numeric extraction below.
    }

    return new Set((value.match(/\d+/g) || []).map((item) => String(item).trim()));
};

const normalizeBlockedIdsList = (value) => {
    if (Array.isArray(value)) {
        return new Set(value.map((item) => String(item).trim()).filter(Boolean));
    }

    if (typeof value !== "string" || value.trim() === "") {
        return new Set();
    }

    return new Set(
        value
            .split(",")
            .map((item) => String(item).trim())
            .filter(Boolean)
    );
};

const getFirstDefinedValue = (item, fields, fallback = "—") => {
	for (const field of fields) {
		const value = item?.[field];
		if (value !== undefined && value !== null && value !== "") {
			return value;
		}
	}

	return fallback;
};

const getModelIndex = (model) => {
    const value = getFirstDefinedValue(model, MODEL_ID_FIELDS, null);
    if (value === null || value === undefined || value === "") {
        return null;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? String(value).trim() : numericValue;
};

const getModelDisplayName = (model) => getFirstDefinedValue(model, MODEL_NAME_FIELDS, "Ismeretlen modell");

const getModelDisplayCode = (model) => getFirstDefinedValue(model, MODEL_CODE_FIELDS, "—");

const isModelHiddenInAdminList = (model) => {
    return Number(model?.hide_in_list) === 1;
};

const findMatchingConfig = (model, modelConfigs) => {
    const modelIndex = getModelIndex(model);
    if (modelIndex === null || !Array.isArray(modelConfigs)) {
        return null;
    }

    return modelConfigs.find((config) => Number(config?.mmelimodel) === Number(modelIndex)) || null;
};

const buildAllowedMap = (config) => {
    const allowedMap = Object.fromEntries(AIDFINANCE_COLUMNS.map((column) => [column, null]));
    const allowedEntries = parseJsonArray(config?.sh_med_tps_dsllwd);

    for (const entry of allowedEntries) {
        const code = String(entry?.aidfinance_code || "").trim().toUpperCase();
        if (!AIDFINANCE_COLUMNS.includes(code)) {
            continue;
        }

        const state = String(entry?.is_allowed || "").trim().toLowerCase();
        allowedMap[code] = ALLOWED_STATE_META[state] || null;
    }

    return allowedMap;
};

const buildAccessoryMap = (config, components) => {
    const blockedIds = normalizeComponentBlockedIds(config?.components_dsllwd);

    return components.map((component) => {
        const componentId = String(component?.aidfinance_szam ?? "").trim();
        return {
            component,
            isAllowed: componentId === "" ? null : !blockedIds.has(componentId),
        };
    });
};

const buildMountingTypeMap = (config, mountingTypes) => {
    const allowedEntries = parseJsonArray(config?.mnt_typs_avlb);
    const allowedMap = new Map(
        allowedEntries.map((entry) => [String(entry?.mounting_type_szam ?? "").trim(), Number(entry?.is_allowed) === 1])
    );

    return mountingTypes.map((mountingType) => {
        const typeId = String(mountingType?.mounting_type_szam ?? "").trim();
        return {
            mountingType,
            isAllowed: typeId === "" ? null : (allowedMap.has(typeId) ? allowedMap.get(typeId) : null),
        };
    });
};

const buildCornerTypeMap = (config, cornerTypes) => {
    const allowedEntries = parseJsonArray(config?.corner_types_avlb);
    const allowedMap = new Map(
        allowedEntries.map((entry) => [String(entry?.corner_type_szam ?? "").trim(), String(entry?.optional || "").trim().toLowerCase()])
    );

    return cornerTypes.map((cornerType) => {
        const typeId = String(cornerType?.corner_type_szam ?? "").trim();
        const optionalState = typeId === "" ? null : allowedMap.get(typeId) || null;
        return {
            cornerType,
            optionalState,
            isAllowed: optionalState === null ? null : (CORNER_STATE_META[optionalState] ? optionalState : null),
        };
    });
};

const buildShoeHornModelMap = (config, shoeHornModels) => {
    const allowedEntries = parseJsonArray(config?.shoe_horn_avlb);
    const allowedMap = new Map(
        allowedEntries.map((entry) => [String(entry?.shoe_horn_model_szam ?? "").trim(), String(entry?.optional || "").trim().toLowerCase()])
    );

    return shoeHornModels.map((shoeHornModel) => {
        const modelId = String(shoeHornModel?.shoe_horn_model_szam ?? "").trim();
        const optionalState = modelId === "" ? null : allowedMap.get(modelId) || null;
        return {
            shoeHornModel,
            optionalState,
            isAllowed: optionalState === null ? null : (CORNER_STATE_META[optionalState] ? optionalState : null),
        };
    });
};

const buildLinerMaterialMap = (config, linerMaterials) => {
    const blockedIds = normalizeBlockedIdsList(config?.linrmterls_dsllwd);

    return linerMaterials.map((linerMaterial) => {
        const materialId = String(linerMaterial?.liner_material_szam ?? "").trim();
        return {
            linerMaterial,
            isAllowed: materialId === "" ? null : !blockedIds.has(materialId),
        };
    });
};

const buildSpecialNeedMap = (config) => {
    return SPECIAL_NEED_COLUMNS.map((specialNeed) => ({
        specialNeed,
        isMarked: Number(config?.[specialNeed.key]) === 1,
    }));
};

const getOptionGroups = (options) => Object.entries(options || {}).map(([groupName, groupValue]) => ({
	groupName,
	size: Array.isArray(groupValue) ? groupValue.length : Object.keys(groupValue || {}).length,
}));

const getAccessoryColumns = (components) => [...components].sort((left, right) => Number(left?.aidfinance_szam || 0) - Number(right?.aidfinance_szam || 0));

const getMountingTypeColumns = (mountingTypes) => [...mountingTypes].sort((left, right) => Number(left?.mounting_type_szam || 0) - Number(right?.mounting_type_szam || 0));

const getShoeHornModelColumns = (shoeHornModels) => [...shoeHornModels].sort((left, right) => Number(left?.shoe_horn_model_szam || 0) - Number(right?.shoe_horn_model_szam || 0));

const getCornerTypeColumns = (cornerTypes) => [...cornerTypes].sort((left, right) => Number(left?.corner_type_szam || 0) - Number(right?.corner_type_szam || 0));

const getLinerMaterialColumns = (linerMaterials) => [...linerMaterials].sort((left, right) => Number(left?.liner_material_szam || 0) - Number(right?.liner_material_szam || 0));

const getStemHeightDisplayValue = (config) => {
    const value = config?.max_stem_height;
    if (value === null || value === undefined || value === "") {
        return "";
    }
    if (String(value).trim().toLowerCase() === "null") {
        return "";
    }
    return String(value);
};

const AdminModellKezelo = () => {
    const navigate = useStepNavigate();
    const scrollContainerRef = useRef(null);
    const topHeaderRowRef = useRef(null);
    const panRafRef = useRef(null);
    const panPointRef = useRef({ x: 0, y: 0 });
    const dragStateRef = useRef({
        startX: 0,
        startY: 0,
        startScrollLeft: 0,
        startScrollTop: 0,
        moved: false,
    });

    const [adminModels, setAdminModels] = useState([]);
    const [modellConfigs, setModellConfigs] = useState([]);
    const [allOptions, setAllOptions] = useState({});
    const [modelSearch, setModelSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isPanning, setIsPanning] = useState(false);
    const [stickySubHeaderTop, setStickySubHeaderTop] = useState(34);

    useEffect(() => {
        fetchModelData();
    }, []);

    const fetchModelData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const [modelConfigsResponse, allOptionsResponse, adminModelsResponse] = await Promise.all([
                dataService.getModelConfigs(),
                dataService.getAllOptions(),
                dataService.getAdminModels(),
            ]);
            
            const modelConfigList = normalizeListPayload(modelConfigsResponse);
            const optionGroups = normalizeObjectPayload(allOptionsResponse);
            const modelList = normalizeListPayload(adminModelsResponse).filter((model) => !isModelHiddenInAdminList(model));

            setModellConfigs(modelConfigList);
            setAllOptions(optionGroups);
            setAdminModels(modelList);

            showSuccessToast('Modell adatok, konfigurációk és szótárak sikeresen betöltve!');
        } catch (err) {
            console.error('Modell adatok fetch error:', err);
            setError(err.message || 'Hiba történt az adatok betöltése során');
            showErrorToast('Hiba történt az adatok betöltése során: ' + (err.message || err));
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/Admin');
    };

    const handleRefresh = () => {
        fetchModelData();
    };

    const pairedModels = useMemo(() => {
        return adminModels.map((model, index) => {
            const matchedConfig = findMatchingConfig(model, modellConfigs);
            const modelKey = getModelIndex(model);
            const rowKey = modelKey === null || modelKey === undefined || modelKey === ""
                ? `model-row-${index}`
                : String(modelKey);

            return {
                model,
                modelKey,
                rowKey,
                matchedConfig,
                isConfigured: Boolean(matchedConfig),
                allowedMap: matchedConfig ? buildAllowedMap(matchedConfig) : Object.fromEntries(AIDFINANCE_COLUMNS.map((column) => [column, null])),
            };
        });
    }, [adminModels, modellConfigs]);

    const accessoryComponents = useMemo(() => getAccessoryColumns(Array.isArray(allOptions?.components) ? allOptions.components : []), [allOptions]);
    const accessoryComponentCount = accessoryComponents.length;
    const mountingTypes = useMemo(() => getMountingTypeColumns(Array.isArray(allOptions?.mounting_types) ? allOptions.mounting_types : []), [allOptions]);
    const mountingTypeCount = mountingTypes.length;
    const shoeHornModels = useMemo(() => getShoeHornModelColumns(Array.isArray(allOptions?.shoe_horn_models) ? allOptions.shoe_horn_models : []), [allOptions]);
    const shoeHornModelCount = shoeHornModels.length;
    const cornerTypes = useMemo(() => getCornerTypeColumns(Array.isArray(allOptions?.corner_types) ? allOptions.corner_types : []), [allOptions]);
    const cornerTypeCount = cornerTypes.length;
    const linerMaterials = useMemo(() => getLinerMaterialColumns(Array.isArray(allOptions?.linermaterials) ? allOptions.linermaterials : []), [allOptions]);
    const linerMaterialCount = linerMaterials.length;
    const specialNeedCount = SPECIAL_NEED_COLUMNS.length;
    const stemHeightCount = 1;
    const dynamicColumnCount = AIDFINANCE_COLUMNS.length + accessoryComponentCount + mountingTypeCount + shoeHornModelCount + cornerTypeCount + linerMaterialCount + specialNeedCount + stemHeightCount;

    const aidfinanceStartColIndex = 1;
    const accessoryStartColIndex = aidfinanceStartColIndex + AIDFINANCE_COLUMNS.length;
    const mountingStartColIndex = accessoryStartColIndex + accessoryComponentCount;
    const shoeHornStartColIndex = mountingStartColIndex + mountingTypeCount;
    const cornerTypeStartColIndex = shoeHornStartColIndex + shoeHornModelCount;
    const linerMaterialStartColIndex = cornerTypeStartColIndex + cornerTypeCount;
    const specialNeedStartColIndex = linerMaterialStartColIndex + linerMaterialCount;
    const stemHeightStartColIndex = specialNeedStartColIndex + specialNeedCount;
    const sectionStartColumns = useMemo(() => {
        const starts = [aidfinanceStartColIndex];

        if (accessoryComponentCount > 0) starts.push(accessoryStartColIndex);
        if (mountingTypeCount > 0) starts.push(mountingStartColIndex);
        if (shoeHornModelCount > 0) starts.push(shoeHornStartColIndex);
        if (cornerTypeCount > 0) starts.push(cornerTypeStartColIndex);
        if (linerMaterialCount > 0) starts.push(linerMaterialStartColIndex);
        if (specialNeedCount > 0) starts.push(specialNeedStartColIndex);
        if (stemHeightCount > 0) starts.push(stemHeightStartColIndex);

        return new Set(starts);
    }, [
        aidfinanceStartColIndex,
        accessoryComponentCount,
        accessoryStartColIndex,
        mountingTypeCount,
        mountingStartColIndex,
        shoeHornModelCount,
        shoeHornStartColIndex,
        cornerTypeCount,
        cornerTypeStartColIndex,
        linerMaterialCount,
        linerMaterialStartColIndex,
        specialNeedCount,
        specialNeedStartColIndex,
        stemHeightCount,
        stemHeightStartColIndex,
    ]);

    const stickyHeaderTop = 0;
    const accessoryRows = useMemo(() => {
        return pairedModels.map((entry) => ({
            ...entry,
            accessoryMap: entry.matchedConfig ? buildAccessoryMap(entry.matchedConfig, accessoryComponents) : [],
            mountingTypeMap: entry.matchedConfig ? buildMountingTypeMap(entry.matchedConfig, mountingTypes) : [],
            shoeHornModelMap: entry.matchedConfig ? buildShoeHornModelMap(entry.matchedConfig, shoeHornModels) : [],
            cornerTypeMap: entry.matchedConfig ? buildCornerTypeMap(entry.matchedConfig, cornerTypes) : [],
            linerMaterialMap: entry.matchedConfig ? buildLinerMaterialMap(entry.matchedConfig, linerMaterials) : [],
            specialNeedMap: entry.matchedConfig ? buildSpecialNeedMap(entry.matchedConfig) : [],
        }));
    }, [pairedModels, accessoryComponents, mountingTypes, shoeHornModels, cornerTypes, linerMaterials]);

    const filteredRows = useMemo(() => {
        const query = modelSearch.trim().toLowerCase();
        if (!query) {
            return accessoryRows;
        }

        return accessoryRows.filter((entry) => {
            const model = entry.model || {};
            const name = String(getModelDisplayName(model) || "").toLowerCase();
            const code = String(getModelDisplayCode(model) || "").toLowerCase();
            const num = String(getFirstDefinedValue(model, ["szam", "id", "modell_szam", "model_id"], "") || "").toLowerCase();
            const key = String(entry.modelKey || "").toLowerCase();

            return name.includes(query) || code.includes(query) || num.includes(query) || key.includes(query);
        });
    }, [accessoryRows, modelSearch]);

    useEffect(() => {
        const updateStickyOffsets = () => {
            if (!topHeaderRowRef.current) {
                return;
            }

            const rowHeight = Math.ceil(topHeaderRowRef.current.getBoundingClientRect().height);
            if (rowHeight > 0) {
                setStickySubHeaderTop(rowHeight);
            }
        };

        updateStickyOffsets();
        window.addEventListener("resize", updateStickyOffsets);
        return () => {
            window.removeEventListener("resize", updateStickyOffsets);
        };
    }, [accessoryComponentCount, mountingTypeCount, shoeHornModelCount, cornerTypeCount, linerMaterialCount, specialNeedCount]);

    const formatModelCellLabel = (model) => {
        const name = getModelDisplayName(model);
        const code = getModelDisplayCode(model);
        return `${name} / ${code}`;
    };

    const handlePanStart = (event) => {
        if (event.button !== 0 || !scrollContainerRef.current) {
            return;
        }

        dragStateRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            startScrollLeft: scrollContainerRef.current.scrollLeft,
            startScrollTop: scrollContainerRef.current.scrollTop,
            moved: false,
        };
        panPointRef.current = { x: event.clientX, y: event.clientY };
        setIsPanning(true);
    };

    const handlePanMove = (event) => {
        if (!isPanning || !scrollContainerRef.current) {
            return;
        }
        panPointRef.current = { x: event.clientX, y: event.clientY };

        if (panRafRef.current === null) {
            panRafRef.current = window.requestAnimationFrame(() => {
                if (!scrollContainerRef.current) {
                    panRafRef.current = null;
                    return;
                }

                const deltaX = panPointRef.current.x - dragStateRef.current.startX;
                const deltaY = panPointRef.current.y - dragStateRef.current.startY;

                if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
                    dragStateRef.current.moved = true;
                }

                scrollContainerRef.current.scrollLeft = dragStateRef.current.startScrollLeft - deltaX;
                scrollContainerRef.current.scrollTop = dragStateRef.current.startScrollTop - deltaY;
                panRafRef.current = null;
            });
        }

        event.preventDefault();
    };

    const handlePanEnd = () => {
        if (panRafRef.current !== null) {
            window.cancelAnimationFrame(panRafRef.current);
            panRafRef.current = null;
        }
        setIsPanning(false);
    };

    const handleModelNumberClick = (event, model) => {
        event.stopPropagation();

        const modelNumber = getFirstDefinedValue(model, ["szam"], null);
        if (modelNumber === null || modelNumber === undefined || modelNumber === "") {
            showErrorToast("A modell száma nem elérhető, ezért nem lehet megnyitni a szerkesztő oldalt.");
            return;
        }

        navigate(`/Admin/Modell/${modelNumber}`, {
            state: {
                adminModels,
                modellConfigs,
                allOptions,
            },
        });
    };

    const getInteractiveCellStyle = ({ colIndex, stickyLeft = false, stickyTop = null, baseStyle = {} }) => {
        const hasSectionSeparator = typeof colIndex === "number" && sectionStartColumns.has(colIndex);
        const separatorStyle = hasSectionSeparator
            ? (stickyTop !== null
                ? { boxShadow: "inset 2px 0 0 #000" }
                : { borderLeft: "2px solid #000" })
            : {};

        const stickyBottomLine = stickyTop !== null ? { boxShadow: `${separatorStyle.boxShadow ? `${separatorStyle.boxShadow}, ` : ""}inset 0 -1px 0 #dee2e6` } : {};

        return {
            ...baseStyle,
            ...(stickyLeft ? { position: "sticky", left: 0, zIndex: stickyTop !== null ? 50 : 40 } : {}),
            ...(stickyTop !== null ? { position: "sticky", top: stickyTop, zIndex: stickyLeft ? 60 : 45 } : {}),
            ...separatorStyle,
            ...stickyBottomLine,
            backgroundColor: baseStyle.backgroundColor || "#fff",
        };
    };

    if (loading) {
        return (
            <TablerLayout title="Modell kezelő">
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Betöltés...</span>
                    </div>
                    <p className="mt-2">Modell konfigurációk betöltése...</p>
                </div>
            </TablerLayout>
        );
    }

    if (error) {
        return (
            <TablerLayout title="Modell kezelő">
                <div className="text-center">
                    <div className="alert alert-danger">
                        <h4>Hiba történt!</h4>
                        <p>{error}</p>
                        <button 
                            className="btn btn-primary me-2" 
                            onClick={handleRefresh}
                        >
                            Újrapróbálkozás
                        </button>
                        <button 
                            className="btn btn-secondary" 
                            onClick={handleBack}
                        >
                            Vissza
                        </button>
                    </div>
                </div>
            </TablerLayout>
        );
    }

    return (
		<TablerLayout title="Modell kezelő" fullWidth hideHeader>
			<div className="row justify-content-center">
				<div className="col-12" style={{ maxWidth: "90vw" }}>
					<div className="card">
                        <div className="card-header d-flex align-items-center position-relative">
                            <div className="position-absolute start-0 d-flex align-items-center ps-2">
                                <button className="btn btn-secondary" onClick={handleBack}>
                                Vissza az Admin oldalra
                                </button>
                            </div>
                            <div className="flex-grow-1 text-center">
                                <h3 className="card-title mb-0">Modell kezelő</h3>
                                <small className="text-muted">
                                Modellek, configok és statikus szótárak előkészített párosítása
                                </small>
                            </div>
                            <div className="position-absolute end-0 d-flex align-items-center gap-2 pe-2">
                                <input
                                type="search"
                                className="form-control form-control-sm"
                                style={{ width: "260px" }}
                                placeholder="Modell keresése (név / kód / szám)"
                                value={modelSearch}
                                onChange={(event) => setModelSearch(event.target.value)}
                                />
                                <button
                                className="btn btn-primary btn-sm"
                                onClick={handleRefresh}
                                disabled={loading}
                                >
                                Frissítés
                                </button>
                            </div>
                        </div>
						<div className="card-body">
							{pairedModels.length === 0 ? (
								<div className="text-center py-4">
									<p className="text-muted">Nincsenek elérhető modellek vagy konfigurációk.</p>
								</div>
							) : filteredRows.length === 0 ? (
								<div className="text-center py-4">
									<p className="text-muted">Nincs találat a megadott keresésre.</p>
								</div>
							) : (
								<div className="row g-3">
									<div className="col-12">
										<div
											ref={scrollContainerRef}
											className="table-responsive border rounded"
											style={{ maxHeight: "70vh", overflow: "auto", cursor: isPanning ? "grabbing" : "grab", userSelect: isPanning ? "none" : "auto" }}
											onMouseDown={handlePanStart}
											onMouseMove={handlePanMove}
											onMouseUp={handlePanEnd}
											onMouseLeave={() => {
												handlePanEnd();
											}}
										>
											<table className="table table-vcenter table-sm align-middle" style={{ tableLayout: "fixed", width: "max-content", borderCollapse: "separate", borderSpacing: 0 }}>
												<thead>
													<tr ref={topHeaderRowRef}>
														<th
															rowSpan={2}
															className="text-center small"
															style={getInteractiveCellStyle({
																colIndex: 0,
																stickyLeft: true,
																stickyTop: stickyHeaderTop,
																baseStyle: { width: "160px", minWidth: "160px", borderRight: "1px solid #dee2e6", borderLeft: "2px solid #000", verticalAlign: "middle" },
															})}
														>
															<div className="d-inline-block fw-semibold" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", letterSpacing: "0.3px", lineHeight: 1.1 }}>
																Modell / kód [#szám]
															</div>
														</th>
														<th colSpan={AIDFINANCE_COLUMNS.length} className="text-center small" style={getInteractiveCellStyle({ colIndex: aidfinanceStartColIndex, stickyTop: stickyHeaderTop })}>
															C0, C2-C5
														</th>
														{accessoryComponentCount > 0 ? (
															<th colSpan={accessoryComponentCount} className="text-center small" style={getInteractiveCellStyle({ colIndex: accessoryStartColIndex, stickyTop: stickyHeaderTop })}>
																vényes tartozékok
															</th>
														) : null}
														{mountingTypeCount > 0 ? (
															<th colSpan={mountingTypeCount} className="text-center small" style={getInteractiveCellStyle({ colIndex: mountingStartColIndex, stickyTop: stickyHeaderTop })}>
																Rögzítés típus
															</th>
														) : null}
                                                            {shoeHornModelCount > 0 ? (
                                                                <th colSpan={shoeHornModelCount} className="text-center small" style={getInteractiveCellStyle({ colIndex: shoeHornStartColIndex, stickyTop: stickyHeaderTop })}>
                                                                    Orrforma
                                                                </th>
                                                            ) : null}
														{cornerTypeCount > 0 ? (
															<th colSpan={cornerTypeCount} className="text-center small" style={getInteractiveCellStyle({ colIndex: cornerTypeStartColIndex, stickyTop: stickyHeaderTop })}>
																Talp
															</th>
														) : null}
														{linerMaterialCount > 0 ? (
															<th colSpan={linerMaterialCount} className="text-center small" style={getInteractiveCellStyle({ colIndex: linerMaterialStartColIndex, stickyTop: stickyHeaderTop })}>
																Bélés típus
															</th>
														) : null}
														<th colSpan={specialNeedCount} className="text-center small" style={getInteractiveCellStyle({ colIndex: specialNeedStartColIndex, stickyTop: stickyHeaderTop })}>
															Speciális igények
														</th>
														<th colSpan={stemHeightCount} className="text-center small" style={getInteractiveCellStyle({ colIndex: stemHeightStartColIndex, stickyTop: stickyHeaderTop })}>
															
														</th>
													</tr>
													<tr>
														{AIDFINANCE_COLUMNS.map((column, columnIndex) => (
															<th
																key={column}
																className="text-center p-0"
																style={getInteractiveCellStyle({
																	colIndex: aidfinanceStartColIndex + columnIndex,
																	stickyTop: stickySubHeaderTop,
																	baseStyle: { width: "24px", minWidth: "24px", height: "96px", verticalAlign: "bottom" },
																})}
															>
																<div className="d-inline-block small fw-semibold text-nowrap" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", whiteSpace: "nowrap", lineHeight: 1, maxWidth: "24px" }}>
																	{column}
																</div>
															</th>
														))}
														{accessoryComponentCount > 0
															? accessoryComponents.map((component, columnIndex) => (
																	<th
																		key={component.aidfinance_szam}
																		className="text-center p-0"
																		style={getInteractiveCellStyle({
																			colIndex: accessoryStartColIndex + columnIndex,
																			stickyTop: stickySubHeaderTop,
																			baseStyle: { width: "28px", minWidth: "28px", height: "120px", verticalAlign: "bottom" },
																		})}
																	>
																		<div className="d-inline-block small fw-semibold text-nowrap" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", whiteSpace: "nowrap", lineHeight: 1, maxWidth: "28px" }}>
																			{component.aidfinance_code} / {component.aidfinance_national}
																		</div>
																	</th>
																))
															: null}
														{mountingTypeCount > 0
															? mountingTypes.map((mountingType, columnIndex) => (
																	<th
																		key={mountingType.mounting_type_szam}
																		className="text-center p-0"
																		style={getInteractiveCellStyle({
																			colIndex: mountingStartColIndex + columnIndex,
																			stickyTop: stickySubHeaderTop,
																			baseStyle: { width: "30px", minWidth: "30px", height: "120px", verticalAlign: "bottom" },
																		})}
																	>
																		<div className="d-inline-block small fw-semibold text-nowrap" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", whiteSpace: "nowrap", lineHeight: 1, maxWidth: "30px" }}>
																			{mountingType.mounting_type_name}
																		</div>
																	</th>
																))
															: null}
                                                            {shoeHornModelCount > 0
                                                                ? shoeHornModels.map((shoeHornModel, columnIndex) => (
                                                                        <th
                                                                            key={shoeHornModel.shoe_horn_model_szam}
                                                                            className="text-center p-0"
                                                                            style={getInteractiveCellStyle({
                                                                                colIndex: shoeHornStartColIndex + columnIndex,
                                                                                stickyTop: stickySubHeaderTop,
                                                                                baseStyle: { width: "30px", minWidth: "30px", height: "120px", verticalAlign: "bottom" },
                                                                            })}
                                                                        >
                                                                            <div className="d-inline-block small fw-semibold text-nowrap" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", whiteSpace: "nowrap", lineHeight: 1, maxWidth: "30px" }}>
                                                                                {shoeHornModel.shoe_horn_model_name}
                                                                            </div>
                                                                        </th>
                                                                    ))
                                                                : null}
														{cornerTypeCount > 0
															? cornerTypes.map((cornerType, columnIndex) => (
																	<th
																		key={cornerType.corner_type_szam}
																		className="text-center p-0"
																		style={getInteractiveCellStyle({
																			colIndex: cornerTypeStartColIndex + columnIndex,
																			stickyTop: stickySubHeaderTop,
																			baseStyle: { width: "30px", minWidth: "30px", height: "120px", verticalAlign: "bottom" },
																		})}
																	>
																		<div className="d-inline-block small fw-semibold text-nowrap" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", whiteSpace: "nowrap", lineHeight: 1, maxWidth: "30px" }}>
																			{cornerType.corner_type_name}
																		</div>
																	</th>
																))
															: null}
														{linerMaterialCount > 0
															? linerMaterials.map((linerMaterial, columnIndex) => (
																	<th
																		key={linerMaterial.liner_material_szam}
																		className="text-center p-0"
																		style={getInteractiveCellStyle({
																			colIndex: linerMaterialStartColIndex + columnIndex,
																			stickyTop: stickySubHeaderTop,
																			baseStyle: { width: "30px", minWidth: "30px", height: "120px", verticalAlign: "bottom" },
																		})}
																	>
																		<div className="d-inline-block small fw-semibold text-nowrap" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", whiteSpace: "nowrap", lineHeight: 1, maxWidth: "30px" }}>
																			{linerMaterial.liner_material_name}
																		</div>
																	</th>
																))
															: null}
														{SPECIAL_NEED_COLUMNS.map((specialNeed, columnIndex) => (
															<th
																key={specialNeed.key}
																className="text-center p-0"
																style={getInteractiveCellStyle({
																	colIndex: specialNeedStartColIndex + columnIndex,
																	stickyTop: stickySubHeaderTop,
																	baseStyle: { width: "30px", minWidth: "30px", height: "120px", verticalAlign: "bottom" },
																})}
															>
																<div className="d-inline-block small fw-semibold text-nowrap" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", whiteSpace: "nowrap", lineHeight: 1, maxWidth: "30px" }}>
																	{specialNeed.label}
																</div>
															</th>
														))}
														<th
															className="text-center p-0"
															style={getInteractiveCellStyle({
																colIndex: stemHeightStartColIndex,
																stickyTop: stickySubHeaderTop,
																baseStyle: { width: "44px", minWidth: "44px", height: "120px", verticalAlign: "bottom" },
															})}
														>
															<div className="d-inline-block small fw-semibold text-nowrap" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", whiteSpace: "nowrap", lineHeight: 1, maxWidth: "44px" }}>
																Max. szármagasság
															</div>
														</th>
													</tr>
												</thead>
												<tbody>
													{filteredRows.map((entry) => {
														const model = entry.model;
                                                        const modelNumber = getFirstDefinedValue(model, ["szam"], "?");

														if (!entry.isConfigured) {
															return (
																<tr key={entry.rowKey} className="text-muted">
																	<td
																		style={getInteractiveCellStyle({
																			colIndex: 0,
																			stickyLeft: true,
																			baseStyle: { borderRight: "1px solid #dee2e6" },
																		})}
																	>
                                                                        <button className="btn btn-secondary p-0 m-0" onClick={(event) => handleModelNumberClick(event, model)}>
                                                                            {formatModelCellLabel(model)}  <span className="small">[#{modelNumber}]</span>
                                                                        </button>
																	</td>
																	<td colSpan={dynamicColumnCount} className="text-center py-3">
																		nincsen a modellnek felvett konfigurációja
																	</td>
																</tr>
															);
														}

														return (
                                                            <tr key={entry.rowKey}>
																<td
																	style={getInteractiveCellStyle({
																		colIndex: 0,
																		stickyLeft: true,
																		baseStyle: { borderRight: "1px solid #dee2e6" },
																	})}
																>
                                                                    <button className="btn btn-secondary p-0 m-0" onClick={(event) => handleModelNumberClick(event, model)}>
                                                                            {formatModelCellLabel(model)} <span className="small">[#{modelNumber}]</span>
                                                                    </button>
																</td>
																{AIDFINANCE_COLUMNS.map((column, columnIndex) => {
																	const meta = entry.allowedMap[column];
																	const colIndex = aidfinanceStartColIndex + columnIndex;

																	if (!meta) {
																		return (
																			<td key={column} className="text-muted text-center px-0 py-0" style={getInteractiveCellStyle({ colIndex, baseStyle: { width: "24px", minWidth: "24px" } })}>
																				—
																			</td>
																		);
																	}

																	return (
																		<td key={column} className="text-center px-0 py-0" style={getInteractiveCellStyle({ colIndex, baseStyle: { width: "24px", minWidth: "24px" } })}>
																			<span className={`badge ${meta.className} p-1`} title={meta.label}>
																				{meta.symbol}
																			</span>
																		</td>
																	);
																})}
																{entry.accessoryMap.map(({ component, isAllowed }, columnIndex) => (
																	<td key={component.aidfinance_szam} className="text-center px-0 py-0" style={getInteractiveCellStyle({ colIndex: accessoryStartColIndex + columnIndex, baseStyle: { width: "28px", minWidth: "28px" } })}>
																		{isAllowed === null ? (
																			<span className="text-muted">—</span>
																		) : isAllowed ? (
																			<span className="badge bg-success p-1" title="Engedélyezve">
																				✓
																			</span>
																		) : (
																			<span className="badge bg-danger p-1" title="Tiltva">
																				✕
																			</span>
																		)}
																	</td>
																))}
																{entry.mountingTypeMap.map(({ mountingType, isAllowed }, columnIndex) => (
																	<td key={mountingType.mounting_type_szam} className="text-center px-0 py-0" style={getInteractiveCellStyle({ colIndex: mountingStartColIndex + columnIndex, baseStyle: { width: "30px", minWidth: "30px" } })}>
																		{isAllowed === null ? (
																			<span className="text-muted">—</span>
																		) : isAllowed ? (
																			<span className="badge bg-success p-1" title="Engedélyezve">
																				✓
																			</span>
																		) : (
																			<span className="badge bg-danger p-1" title="Tiltva">
																				✕
																			</span>
																		)}
																	</td>
																))}
                                                                {entry.shoeHornModelMap.map(({ shoeHornModel, optionalState }, columnIndex) => {
                                                                    const meta = CORNER_STATE_META[optionalState] || null;

                                                                    return (
                                                                        <td key={shoeHornModel.shoe_horn_model_szam} className="text-center px-0 py-0" style={getInteractiveCellStyle({ colIndex: shoeHornStartColIndex + columnIndex, baseStyle: { width: "30px", minWidth: "30px" } })}>
                                                                            {meta ? (
                                                                                <span className={`badge ${meta.className} p-1`} title={meta.label}>
                                                                                    {meta.symbol}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-muted">—</span>
                                                                            )}
                                                                        </td>
                                                                    );
                                                                })}
																{entry.cornerTypeMap.map(({ cornerType, isAllowed, optionalState }, columnIndex) => {
																	const meta = CORNER_STATE_META[optionalState] || null;

																	return (
																		<td key={cornerType.corner_type_szam} className="text-center px-0 py-0" style={getInteractiveCellStyle({ colIndex: cornerTypeStartColIndex + columnIndex, baseStyle: { width: "30px", minWidth: "30px" } })}>
																			{meta ? (
																				<span className={`badge ${meta.className} p-1`} title={meta.label}>
																					{meta.symbol}
																				</span>
																			) : (
																				<span className="text-muted">—</span>
																			)}
																		</td>
																	);
																})}
																{entry.linerMaterialMap.map(({ linerMaterial, isAllowed }, columnIndex) => (
																	<td key={linerMaterial.liner_material_szam} className="text-center px-0 py-0" style={getInteractiveCellStyle({ colIndex: linerMaterialStartColIndex + columnIndex, baseStyle: { width: "30px", minWidth: "30px" } })}>
																		{isAllowed === null ? (
																			<span className="text-muted">—</span>
																		) : isAllowed ? (
																			<span className="badge bg-success p-1" title="Engedélyezve">
																				✓
																			</span>
																		) : (
																			<span className="badge bg-danger p-1" title="Tiltva">
																				✕
																			</span>
																		)}
																	</td>
																))}
																{entry.specialNeedMap.map(({ specialNeed, isMarked }, columnIndex) => (
																	<td key={specialNeed.key} className="text-center px-0 py-0" style={getInteractiveCellStyle({ colIndex: specialNeedStartColIndex + columnIndex, baseStyle: { width: "30px", minWidth: "30px" } })}>
																		{isMarked ? (
																			<span className="fw-bold text-dark" title="Jelölve">
																				✕
																			</span>
																		) : null}
																	</td>
																))}
																<td className="text-center px-0 py-0" style={getInteractiveCellStyle({ colIndex: stemHeightStartColIndex, baseStyle: { width: "44px", minWidth: "44px" } })}>
																	<span className="small">{getStemHeightDisplayValue(entry.matchedConfig)}</span>
																</td>
															</tr>
														);
													})}
												</tbody>
											</table>
										</div>
									</div>
								</div>
							)}
						</div>

						<div className="card-footer p-2">
                            <button className="btn btn-secondary" onClick={handleBack}>
                                Vissza az Admin oldalra
                            </button>
						</div>
					</div>
				</div>
			</div>
		</TablerLayout>
	);
};

export default AdminModellKezelo;